package ai.jessie.web.customers

import ai.jessie.shared.customers.CustomerResponse
import ai.jessie.web.theme.BadgeTone
import ai.jessie.web.theme.CalendarScrollStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.CustomerTableRowStyle
import ai.jessie.web.theme.CustomerTableStyle
import ai.jessie.web.theme.GroupTableRowVariant
import ai.jessie.web.theme.HeadTableRowVariant
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.TableIconStyle
import ai.jessie.web.theme.TableRowKind
import ai.jessie.web.theme.TableRowStyle
import ai.jessie.web.theme.TableSpanStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.TableStyle
import ai.jessie.web.theme.badge
import ai.jessie.web.theme.icon
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// Step 2, A11: only the customers the server returned for this organisation.
@Composable
internal fun customerList(
    organisationId: String,
    state: CustomerUiState,
    actions: CustomerActions,
) {
    val customers = state.customers
    if (customers != null) {
        if (customers.isEmpty())
            P(attrs = HintStyle.toAttrs()) { Text("Noch keine Firmen oder Personen.") }
        else customerTable(organisationId, customers)
    }
    Div(attrs = RowStyle.toAttrs()) {
        themedButton("Kundenliste erneut laden", actions.refresh, disabled = state.busy)
    }
}

// Alphabetical groups by the first letter of the name (`15-Firmen.png`); the server's records
// are only reordered for display.
@Composable
private fun customerTable(organisationId: String, customers: List<CustomerResponse>) {
    Div(attrs = CardStyle.toAttrs()) {
        Div(attrs = CalendarScrollStyle.toAttrs()) {
            Div(
                attrs =
                    TableStyle.toModifier().then(CustomerTableStyle.toModifier()).toAttrs {
                        attr("role", "table")
                        attr("aria-label", LIST_TITLE)
                    }
            ) {
                customerRow(HeadTableRowVariant) {
                    listOf("", "Name", "Ort", "Status", "").forEach {
                        Span(attrs = { attr("role", "columnheader") }) { Text(it) }
                    }
                }
                customers
                    .sortedBy { it.displayName.lowercase() }
                    .groupBy { groupLetter(it.displayName) }
                    .forEach { (letter, group) ->
                        customerRow(GroupTableRowVariant) {
                            Span(attrs = TableSpanStyle.toAttrs { attr("role", "cell") }) {
                                Text(letter)
                            }
                        }
                        group.forEach { customerEntryRow(organisationId, it) }
                    }
            }
        }
    }
}

@Composable
private fun customerEntryRow(organisationId: String, customer: CustomerResponse) {
    val href = "/organisations/$organisationId/customers/${customer.id}"
    customerRow(null) {
        Span(attrs = TableIconStyle.toAttrs { attr("role", "cell") }) {
            customerTypeIcon(customer.type)?.let { icon(it) }
        }
        Span(attrs = { attr("role", "cell") }) {
            A(href = href, attrs = TableStrongStyle.toAttrs()) { Text(customer.displayName) }
        }
        Span(attrs = { attr("role", "cell") }) {
            Text(customer.addresses.firstOrNull()?.city ?: "–")
        }
        Span(attrs = { attr("role", "cell") }) {
            if (customer.status == "ARCHIVED")
                badge(statusLabel(customer.status), BadgeTone.NEUTRAL)
        }
        Span(attrs = TableIconStyle.toAttrs { attr("role", "cell") }) {
            A(href = href, attrs = { attr("aria-label", "${customer.displayName} öffnen") }) {
                icon("chevron-right", size = 12)
            }
        }
    }
}

@Composable
private fun customerRow(variant: CssStyleVariant<TableRowKind>?, cells: @Composable () -> Unit) {
    Div(
        attrs =
            TableRowStyle.toModifier(variant).then(CustomerTableRowStyle.toModifier()).toAttrs {
                attr("role", "row")
            }
    ) {
        cells()
    }
}

/** Font Awesome glyph for the customer type: building for companies, user for individuals. */
internal fun customerTypeIcon(type: String): String? =
    when (type) {
        "COMPANY" -> "building"
        "INDIVIDUAL" -> "user"
        else -> null
    }

private fun groupLetter(name: String): String {
    val first = name.trim().firstOrNull()?.uppercaseChar() ?: '#'
    return if (first.isLetter()) first.toString() else "#"
}
